/**
 * Board Background Utilities
 *
 * Helper functions for applying board background styles.
 */

import { BoardPreferences } from '@/types/preferences.types';
import { BackgroundDisplayMode } from '@/types/BackgroundDisplayMode';
import { logger } from '@/utils/logger';

/**
 * Convert board background configuration to CSS styles
 *
 * @param background - Board background configuration from preferences
 * @returns CSS styles object for applying background
 */
export function getBoardBackgroundStyle(
  background?: BoardPreferences['background']
): React.CSSProperties {
  if (!background?.absolute_url) {
    return {};
  }

  logger.debug('[getBoardBackgroundStyle] Called with background:', {
    background,
  });
  logger.debug('[getBoardBackgroundStyle] Display mode:', {
    display_mode: background.display_mode,
  });

  const encodedUrl = encodeURI(background.absolute_url);

  const baseStyle: React.CSSProperties = {
    backgroundImage: `url("${encodedUrl}")`,
    backgroundPosition: 'center',
  };

  switch (background.display_mode || BackgroundDisplayMode.COVER) {
    case BackgroundDisplayMode.COVER:
      return {
        ...baseStyle,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      };
    case BackgroundDisplayMode.CONTAIN:
      return {
        ...baseStyle,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
      };
    case BackgroundDisplayMode.TILE:
      return {
        ...baseStyle,
        backgroundSize: 'auto',
        backgroundRepeat: 'repeat',
      };
    case BackgroundDisplayMode.CENTER:
      return {
        ...baseStyle,
        backgroundSize: 'auto',
        backgroundRepeat: 'no-repeat',
      };
    case BackgroundDisplayMode.STRETCH:
      return {
        ...baseStyle,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
      };
    default:
      return {
        ...baseStyle,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      };
  }
}
